import React, { useContext } from 'react'
import moment from 'moment'

import DateRange from '../DateRange'
import { DateControlContext } from 'contexts/DateControlContext'
import { StandardDateControl } from './StandardDateControl'

import * as S from './DateControl.styles'

export const DATE_PICKER_FREQUENCIES = {
  default: 'default',
  week: 'week',
  month: 'month',
  custom: 'custom',
}

type Props = {
  handleExport?: () => void
  isCarecoAdmin?: boolean
  customDateVariant?: 'below' | 'inlineTab'
  /** When true, hide Default / 7 days / 30 days / Custom (Export still shown if provided). */
  hideFrequency?: boolean
}

const CarecoDateControl: React.FC<Pick<Props, 'handleExport' | 'hideFrequency'>> = ({
  handleExport,
  hideFrequency,
}) => {
  const { startDate, endDate, frequency, onChange, resetDate } = useContext(DateControlContext)

  const handleChange = frequency => {
    let newStartDate, newEndDate
    switch (frequency) {
      case DATE_PICKER_FREQUENCIES.week: {
        newStartDate = moment().subtract(1, 'week').add(1, 'day')
        newEndDate = moment()
        break
      }
      case DATE_PICKER_FREQUENCIES.month: {
        newStartDate = moment().subtract(1, 'month').add(1, 'day')
        newEndDate = moment()
        break
      }
      case DATE_PICKER_FREQUENCIES.custom: {
        newStartDate = startDate
        newEndDate = endDate
        break
      }
    }

    onChange({ startDate: newStartDate, endDate: newEndDate, frequency })
  }

  const handleCustomDate = (startDate, endDate) => {
    onChange({ startDate, endDate, frequency: DATE_PICKER_FREQUENCIES.custom })
  }

  const isCustom = frequency === DATE_PICKER_FREQUENCIES.custom

  return (
    <S.DateControl $isCarecoAdmin>
      <S.ControlsColumn>
        <S.ControlRow>
          {!hideFrequency && (
            <S.FrequencyButtons $isCarecoAdmin>
              <S.FrequencyButton
                onClick={() => resetDate()}
                isSelected={frequency === DATE_PICKER_FREQUENCIES.default}
                isCarecoAdmin
              >
                Default
              </S.FrequencyButton>
              <S.FrequencyButton
                onClick={() => handleChange(DATE_PICKER_FREQUENCIES.week)}
                isSelected={frequency === DATE_PICKER_FREQUENCIES.week}
                isCarecoAdmin
              >
                7 days
              </S.FrequencyButton>
              <S.FrequencyButton
                onClick={() => handleChange(DATE_PICKER_FREQUENCIES.month)}
                isSelected={frequency === DATE_PICKER_FREQUENCIES.month}
                isCarecoAdmin
              >
                30 days
              </S.FrequencyButton>
              <S.FrequencyButton
                onClick={() => handleChange(DATE_PICKER_FREQUENCIES.custom)}
                isSelected={isCustom}
                isCarecoAdmin
              >
                Custom
              </S.FrequencyButton>
            </S.FrequencyButtons>
          )}

          {handleExport && (
            <S.ExportButton $isCarecoAdmin onClick={handleExport}>
              Export
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="2" />
              </svg>
            </S.ExportButton>
          )}
        </S.ControlRow>

        {!hideFrequency && isCustom && (
          <S.CustomDateRangeWrap $isCarecoAdmin>
            <DateRange startDate={startDate} endDate={endDate} setDate={handleCustomDate} />
          </S.CustomDateRangeWrap>
        )}
      </S.ControlsColumn>
    </S.DateControl>
  )
}

export const DateControl: React.FC<Props> = ({
  handleExport,
  isCarecoAdmin,
  customDateVariant,
  hideFrequency,
}) => {
  if (!isCarecoAdmin) {
    return <StandardDateControl customDateVariant={customDateVariant} />
  }

  return <CarecoDateControl handleExport={handleExport} hideFrequency={hideFrequency} />
}
